import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '@/react-query/api';

import { endpoints } from '../endpoints';
import type { GetCombinedAppointmentsResponse } from './useCombinedAppointments';

const REFETCH_INTERVAL_IN_SECONDS = 30;

interface RecentNotesSearchRequest {
  // optional filters for patient name
  patientOpts?: {
    name?: string;
  };

  meta?: {
    // optional pagination and cursor info
    pagination?: {
      cursor?: string;
      limit?: number;
    };

    // optional sort field and direction
    sort?: {
      [field: string]: 'ASC' | 'DESC';
    };
  };
}

/**
 * Fetches recent notes sorted by creation date (descending).
 * Uses the /videa-notes/search-notes endpoint to get appointments with notes
 * ordered by when the notes were created, rather than appointment date.
 * Supports filtering by patient name.
 *
 * @param args - Optional patient search, pagination and sort parameters
 * @param options - React Query options
 * @returns Infinite query result with appointments and notes
 */
export const useRecentNotes = (
  args: RecentNotesSearchRequest = {},
  options?: UseInfiniteQueryOptions<GetCombinedAppointmentsResponse, unknown>,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const practiceId = currentPractice;
  const orgId = currentOrg;

  return useInfiniteQuery({
    queryKey: [
      endpoints.ambient.voiceNotes.searchNotesByCreatedAt,
      args,
      practiceId,
    ],
    queryFn: ({ pageParam }) =>
      makeAPIRequestReactQuery<GetCombinedAppointmentsResponse>(
        endpoints.ambient.voiceNotes.searchNotesByCreatedAt,
        {
          ...args,
          meta: {
            ...args?.meta,
            pagination: {
              cursor: pageParam,
              limit: args?.meta?.pagination?.limit || 10,
            },
            sort: {
              createdAt: 'DESC', // Always sort by creation date descending
            },
          },
        },
        'get',
        { headers: { practice: practiceId, org: orgId } },
      ),
    getNextPageParam: (lastPage) => lastPage?.nextCursor,
    refetchInterval: REFETCH_INTERVAL_IN_SECONDS * 1000,
    ...options,
    // Combine caller's enabled condition with internal safety checks
    enabled: (options?.enabled ?? true) && Boolean(practiceId) && Boolean(orgId),
  });
};
